import { useEffect, useRef } from 'react';

/**
 * Custom hook that adds scroll-reveal animations using IntersectionObserver.
 * Applies the 'revealed' class to elements with 'scroll-reveal' class
 * when they enter the viewport.
 *
 * @param {Object} options
 * @param {number} options.threshold - Visibility threshold (0–1), default 0.1
 * @param {string} options.rootMargin - Observer root margin, default '0px 0px -40px 0px'
 */
export default function useScrollReveal({ threshold = 0.1, rootMargin = '0px 0px -40px 0px' } = {}) {
    const containerRef = useRef(null);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const elements = container.querySelectorAll('.scroll-reveal');
        if (!elements.length) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('revealed');
                        observer.unobserve(entry.target);
                    }
                });
            },
            { threshold, rootMargin }
        );

        elements.forEach((el) => observer.observe(el));

        return () => observer.disconnect();
    }, [threshold, rootMargin]);

    return containerRef;
}
